import { createTheme } from '@mui/material/styles'
import type { CSSProperties } from 'react'
import { searchableSelectMenuProps } from '../components/Select/SearchableMenuList'
import {
  animationTokens,
  borderRadiusTokens,
  colorTokens,
  controlHeightTokens,
  shadowTokens,
  spacingTokens,
} from './tokens'

// Micro radius for tiny indicator surfaces (tooltips, skeletons, progress bars)
// where the 8px control radius reads as a pill.
const microRadius = 4

declare module '@mui/material/styles' {
  interface Palette {
    customColors: {
      green1: string
      green2: string
      green3: string
      green4: string
      darkSlate: string
      darkRed: string
      cardBackground: string
      blueheader: string
      sidebarBackground: string
      sidebarHover: string
      sidebarActive: string
      contentBackground: string
      cardElevation: string
      navigationText: string
      navigationIcon: string
    }
  }
  interface PaletteOptions {
    customColors?: {
      green1?: string
      green2?: string
      green3?: string
      green4?: string
      darkSlate?: string
      darkRed?: string
      cardBackground?: string
      blueheader?: string
      sidebarBackground?: string
      sidebarHover?: string
      sidebarActive?: string
      contentBackground?: string
      cardElevation?: string
      navigationText?: string
      navigationIcon?: string
    }
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    display: CSSProperties
  }

  interface TypographyVariantsOptions {
    display?: CSSProperties
  }

  interface Theme {
    customSpacing: {
      sidebarWidth: number
      sidebarCollapsedWidth: number
      contentPadding: number
      cardPadding: number
      navigationItemHeight: number
      pageMargin: number
      cardGap: number
      sectionGap: number
      elementGap: number
    }
    customShadows: {
      card: string
      cardHover: string
      sidebar: string
      navigation: string
    }
    customAnimations: {
      sidebarTransition: string
      navigationTransition: string
      cardTransition: string
      hoverTransition: string
    }
  }
  interface ThemeOptions {
    customSpacing?: {
      sidebarWidth?: number
      sidebarCollapsedWidth?: number
      contentPadding?: number
      cardPadding?: number
      navigationItemHeight?: number
      pageMargin?: number
      cardGap?: number
      sectionGap?: number
      elementGap?: number
    }
    customShadows?: {
      card?: string
      cardHover?: string
      sidebar?: string
      navigation?: string
    }
    customAnimations?: {
      sidebarTransition?: string
      navigationTransition?: string
      cardTransition?: string
      hoverTransition?: string
    }
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display: true
  }
}

const headingFont = '"system-ui", Arial, sans-serif'

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 500,
      md: 900,
      lg: 1024,
      xl: 1536,
    },
  },

  shape: {
    borderRadius: borderRadiusTokens.control,
  },

  customSpacing: spacingTokens,
  customShadows: shadowTokens,
  customAnimations: animationTokens,

  palette: {
    primary: {
      main: colorTokens.green100,
      dark: colorTokens.green200,
      light: colorTokens.green300,
      contrastText: colorTokens.white,
    },
    secondary: {
      main: colorTokens.slate150,
      dark: colorTokens.slate200,
      contrastText: colorTokens.slate700,
    },
    success: {
      main: colorTokens.green300,
      dark: colorTokens.green100,
      contrastText: colorTokens.white,
    },
    error: {
      main: colorTokens.errorMain,
      dark: colorTokens.errorDark,
      contrastText: colorTokens.white,
    },
    background: {
      default: '#f8f8f8',
      paper: colorTokens.white,
    },
    text: {
      primary: colorTokens.slate900,
      secondary: colorTokens.slate700,
    },
    divider: colorTokens.slate200,
    customColors: {
      green1: colorTokens.green100,
      green2: colorTokens.green300,
      green3: colorTokens.green400,
      green4: colorTokens.green500,
      darkSlate: colorTokens.darkSlate,
      darkRed: colorTokens.darkRed,
      cardBackground: colorTokens.white,
      blueheader: colorTokens.blueHeader,
      sidebarBackground: colorTokens.white,
      sidebarHover: colorTokens.slate100,
      sidebarActive: colorTokens.slate150,
      contentBackground: colorTokens.slate50,
      cardElevation: colorTokens.white,
      navigationText: colorTokens.slate700,
      navigationIcon: colorTokens.slate400,
    },
  },

  typography: {
    fontFamily: '"system-ui", Arial, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    display: {
      fontFamily: headingFont,
      fontWeight: 800,
      lineHeight: 1,
      fontSize: 'clamp(32px, 5vw, 72px)',
      letterSpacing: '-0.035em',
      fontStyle: 'normal',
    },
    h1: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: '2rem',
      letterSpacing: '-0.01em',
      fontStyle: 'normal',
    },
    h2: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
      fontStyle: 'normal',
    },
    h3: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: '1.25rem',
      fontStyle: 'normal',
    },
    h4: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: '1.125rem',
      fontStyle: 'normal',
    },
    h5: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: '1rem',
      fontStyle: 'normal',
    },
    h6: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: '0.875rem',
      fontStyle: 'normal',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      fontStyle: 'normal',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      fontStyle: 'normal',
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      fontStyle: 'normal',
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      fontStyle: 'normal',
    },
    caption: {
      fontWeight: 400,
      fontSize: '0.75rem',
      fontStyle: 'normal',
    },
    overline: {
      fontWeight: 500,
      fontSize: '0.6875rem',
      textTransform: 'uppercase',
      fontStyle: 'normal',
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
      fontStyle: 'normal',
      fontSize: '1rem',
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: `
        :root {
          --ag-background-color: ${colorTokens.white};
          --ag-header-background-color: ${colorTokens.blueHeader};
          --ag-header-foreground-color: ${colorTokens.white};
          --ag-border-color: ${colorTokens.slate200};
          --ag-odd-row-background-color: ${colorTokens.slate50};
          --ag-row-height: 48px;
        }

        em, i, cite, dfn, address, var {
          font-style: normal;
        }

        *:focus-visible {
          outline: 2px solid ${colorTokens.green100};
          outline-offset: 2px;
        }

        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background: ${colorTokens.slate200};
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${colorTokens.slate300};
        }
        * {
          scrollbar-width: thin;
          scrollbar-color: ${colorTokens.slate200} transparent;
        }
      `,
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontWeight: 500,
          textTransform: 'none',
          fontStyle: 'normal',
          fontSize: '1rem',
          borderRadius: borderRadiusTokens.control,
          transition: animationTokens.navigationTransition,
          '&:focus-visible': {
            outline: `2px solid ${colorTokens.green100}`,
            outlineOffset: '2px',
          },
          '&.MuiButton-danger': {
            backgroundColor: colorTokens.errorMain,
            color: colorTokens.white,
            '&:hover': {
              backgroundColor: colorTokens.errorDark,
            },
          },
        },
        sizeSmall: {
          minHeight: controlHeightTokens.small,
          fontSize: '0.875rem',
          padding: '4px 12px',
        },
        sizeMedium: {
          minHeight: controlHeightTokens.medium,
          padding: '6px 16px',
        },
        sizeLarge: {
          minHeight: controlHeightTokens.large,
          fontSize: '1rem',
          padding: '10px 24px',
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: colorTokens.green100,
            color: colorTokens.white,
            border: `1px solid ${colorTokens.green100}`,
            '&:hover': {
              borderColor: colorTokens.green200,
              backgroundColor: colorTokens.green200,
            },
            '&.Mui-disabled': {
              backgroundColor: colorTokens.slate300,
              color: colorTokens.white,
              borderColor: colorTokens.slate300,
            },
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            backgroundColor: colorTokens.slate150,
            color: colorTokens.slate700,
            '&:hover': {
              backgroundColor: colorTokens.slate200,
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            borderColor: colorTokens.green100,
            color: colorTokens.green100,
            '&:hover': {
              backgroundColor: `${colorTokens.green100}14`,
              borderColor: colorTokens.green200,
            },
          },
        },
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            color: colorTokens.green100,
            '&:hover': {
              backgroundColor: colorTokens.slate100,
            },
          },
        },
      ],
    },

    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          '&.admin-card': {
            borderRadius: borderRadiusTokens.surface,
            boxShadow: shadowTokens.card,
            border: `1px solid ${colorTokens.slate200}`,
            transition: animationTokens.cardTransition,
            '&:hover': {
              boxShadow: shadowTokens.cardHover,
            },
          },
          '&.admin-sidebar': {
            boxShadow: shadowTokens.sidebar,
            borderRadius: 0,
          },
        },
        // Uniform surface treatment: every plain Paper gets a slate200 border
        // on background.paper. Excludes components that compose an elevation-0
        // Paper internally and own their chrome (alerts, accordions, cards,
        // drawer panels, autocomplete popups, app bars, sidebars).
        elevation0: {
          '&:not(.MuiAlert-root):not(.MuiAccordion-root):not(.MuiCard-root):not(.MuiDrawer-paper):not(.MuiAutocomplete-paper):not(.MuiAppBar-root):not(.MuiPickersPopper-paper):not(.admin-sidebar)':
            {
              border: `1px solid ${colorTokens.slate200}`,
              borderRadius: borderRadiusTokens.surface,
            },
        },
        elevation1: {
          '&:not(.MuiAppBar-root)': {
            boxShadow: shadowTokens.card,
            border: `1px solid ${colorTokens.slate200}`,
            borderRadius: borderRadiusTokens.surface,
          },
        },
        outlined: {
          borderRadius: borderRadiusTokens.surface,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${colorTokens.slate200}`,
          borderRadius: borderRadiusTokens.control,
          boxShadow: shadowTokens.cardHover,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadiusTokens.control,
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: borderRadiusTokens.surface,
          border: `1px solid ${colorTokens.slate200}`,
          boxShadow: shadowTokens.card,
          backgroundImage: 'none',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacingTokens.cardPadding,
          '&:last-child': {
            paddingBottom: spacingTokens.cardPadding,
          },
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusTokens.control,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colorTokens.slate200,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colorTokens.slate400,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colorTokens.green100,
            borderWidth: 2,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: colorTokens.errorMain,
          },
        },
        input: {
          fontStyle: 'normal',
          '&::placeholder': {
            fontStyle: 'normal',
            opacity: 0.6,
            color: colorTokens.slate400,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontStyle: 'normal',
          fontWeight: 400,
          color: colorTokens.slate500,
          '&.Mui-focused': {
            color: colorTokens.green100,
          },
          '&.Mui-error': {
            color: colorTokens.errorMain,
          },
        },
      },
    },

    MuiSelect: {
      defaultProps: {
        MenuProps: searchableSelectMenuProps(),
      },
      styleOverrides: {
        root: {
          borderRadius: borderRadiusTokens.control,
        },
      },
    },

    MuiListSubheader: {
      styleOverrides: {
        root: {
          '&.searchable-select-filter': {
            backgroundColor: colorTokens.white,
            lineHeight: 'normal',
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 12,
            paddingRight: 12,
            zIndex: 1,
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusTokens.control,
          fontWeight: 500,
          fontStyle: 'normal',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadiusTokens.control,
          fontStyle: 'normal',
          alignItems: 'center',
        },
        message: {
          fontStyle: 'normal',
        },
      },
      variants: [
        {
          props: { severity: 'success', variant: 'standard' },
          style: {
            borderLeft: `4px solid ${colorTokens.green100}`,
            backgroundColor: `${colorTokens.green500}33`,
          },
        },
        {
          props: { severity: 'error', variant: 'standard' },
          style: {
            borderLeft: `4px solid ${colorTokens.errorMain}`,
            backgroundColor: `${colorTokens.errorMain}14`,
          },
        },
        {
          props: { severity: 'warning', variant: 'standard' },
          style: {
            borderLeft: '4px solid #D97706',
          },
        },
        {
          props: { severity: 'info', variant: 'standard' },
          style: {
            borderLeft: `4px solid ${colorTokens.blueHeader}`,
            backgroundColor: `${colorTokens.blueHeader}14`,
          },
        },
      ],
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 400,
          fontStyle: 'normal',
          padding: '10px 16px',
          borderColor: colorTokens.slate200,
        },
        head: {
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: colorTokens.slate700,
          backgroundColor: colorTokens.slate50,
          borderBottom: `2px solid ${colorTokens.slate200}`,
        },
        body: {
          color: colorTokens.slate900,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colorTokens.slate100,
          },
          '&.Mui-selected': {
            backgroundColor: `${colorTokens.green100}14`,
            '&:hover': {
              backgroundColor: `${colorTokens.green100}1f`,
            },
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadiusTokens.surface,
          boxShadow: '0 20px 40px -8px rgba(0,0,0,0.15)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 500,
          fontStyle: 'normal',
          padding: '20px 24px 12px',
          color: colorTokens.slate900,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '12px 24px',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: 8,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colorTokens.slate200,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colorTokens.green100,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          textTransform: 'none',
          fontStyle: 'normal',
          '&.Mui-selected': {
            color: colorTokens.green100,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colorTokens.darkSlate,
          color: colorTokens.white,
          fontSize: '0.75rem',
          fontStyle: 'normal',
          borderRadius: microRadius,
          padding: '6px 10px',
        },
        arrow: {
          color: colorTokens.darkSlate,
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: microRadius,
          backgroundColor: colorTokens.slate100,
          '&.admin-skeleton': {
            backgroundColor: colorTokens.slate100,
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          '&.admin-drawer': {
            borderRadius: 0,
            boxShadow: `4px 0 12px 0 rgba(0,0,0,0.10)`,
          },
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          '&.admin-nav-item': {
            borderRadius: borderRadiusTokens.control,
            margin: '2px 8px',
            minHeight: spacingTokens.navigationItemHeight,
            transition: animationTokens.navigationTransition,
            '&:hover': {
              backgroundColor: colorTokens.slate100,
            },
            '&.active': {
              backgroundColor: colorTokens.slate150,
              borderLeft: `3px solid ${colorTokens.green100}`,
              '& .MuiListItemIcon-root': {
                color: colorTokens.green100,
              },
              '& .MuiListItemText-primary': {
                color: colorTokens.slate700,
                fontWeight: 500,
              },
            },
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          '&.admin-nav-icon': {
            color: colorTokens.slate400,
            minWidth: 40,
            transition: `color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
          },
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        root: {
          '&.admin-nav-text': {
            '& .MuiListItemText-primary': {
              fontSize: '0.875rem',
              fontWeight: 500,
              fontStyle: 'normal',
              color: colorTokens.slate700,
              transition: `color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
            },
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.admin-toggle': {
            borderRadius: borderRadiusTokens.control,
            transition: animationTokens.navigationTransition,
            '&:hover': {
              backgroundColor: colorTokens.slate100,
            },
          },
        },
      },
    },

    MuiTypography: {
      defaultProps: {
        variantMapping: {
          display: 'h1',
        },
      },
      styleOverrides: {
        root: {
          fontStyle: 'normal',
          '&.admin-page-title': {
            fontSize: '1.875rem',
            fontWeight: 700,
            color: colorTokens.slate900,
            marginBottom: '1.5rem',
          },
          '&.admin-section-title': {
            fontSize: '1.25rem',
            fontWeight: 500,
            color: colorTokens.slate700,
            marginBottom: '1rem',
          },
          '&.admin-card-title': {
            fontSize: '1.125rem',
            fontWeight: 500,
            color: colorTokens.slate700,
          },
        },
      },
    },

    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: `1px solid ${colorTokens.slate200}`,
          borderRadius: `${borderRadiusTokens.control}px !important`,
          '&:before': {
            display: 'none',
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontStyle: 'normal',
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 500,
          fontStyle: 'normal',
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontStyle: 'normal',
          marginLeft: 0,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: microRadius,
          backgroundColor: colorTokens.slate200,
        },
        bar: {
          borderRadius: microRadius,
        },
      },
    },

    MuiCircularProgress: {
      defaultProps: {
        color: 'primary',
      },
    },
  },
})

export default theme
